// 📚 References — opened from Consultation (with the visit) or an admission.
// Two sources, as two tabs:
//   Hospital References    — the hospital's approved documents only, cited
//                            (HospitalReferencesPanel; grant clinical-references).
//   Web Search References  — trusted medical websites through a search API,
//                            labelled external (WebSearchPanel; grant
//                            clinical-references:web, contract §6ai).
// Both panels stay mounted while the drawer is open, so switching tabs never
// loses an answer. The last tab used is remembered per browser.
import { useState } from 'react';
import { BookOpen, Lock } from 'lucide-react';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useAuth } from '@/hooks/useAuth';
import { HospitalReferencesPanel } from './HospitalReferencesPanel';
import { WebSearchPanel } from './WebSearchPanel';

type Source = 'hospital' | 'web';
const TAB_KEY = 'hms_references_tab';

function rememberedTab(): Source {
  try { return localStorage.getItem(TAB_KEY) === 'web' ? 'web' : 'hospital'; } catch { return 'hospital'; }
}

function NoAccess({ what }: { what: string }) {
  return (
    <div className="flex items-start gap-1.5 rounded-lg bg-muted px-3 py-2.5 text-[12.5px] text-ink-muted">
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>Your role does not include {what}. An administrator can grant it in Admin → People &amp; Access → Users &amp; Roles.</span>
    </div>
  );
}

export function ReferencesDrawer({
  open, onOpenChange, visitId = null, admissionId = null, patientLabel, onInsert,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visitId?: number | null;
  admissionId?: number | null;
  /** Shown so the doctor knows whose details are used (shown locally only). */
  patientLabel?: string | null;
  onInsert?: (text: string) => void;
}) {
  const { user } = useAuth();
  const modules: string[] = user?.modules ?? [];
  const canHospital = modules.includes('clinical-references');
  const canWeb = modules.includes('clinical-references:web');
  const [tab, setTab] = useState<Source>(() => {
    const t = rememberedTab();
    if (t === 'web' && !canWeb && canHospital) return 'hospital';
    if (t === 'hospital' && !canHospital && canWeb) return 'web';
    return t;
  });
  const choose = (v: string) => {
    const t: Source = v === 'web' ? 'web' : 'hospital';
    setTab(t);
    try { localStorage.setItem(TAB_KEY, t); } catch { /* a remembered tab is a convenience */ }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-teal" /> References</SheetTitle>
          <SheetDescription>Choose the reference source for your clinical question.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          <Tabs value={tab} onValueChange={choose}>
            <TabsList className="grid h-auto w-full grid-cols-2">
              <TabsTrigger value="hospital" className="whitespace-normal py-1.5">Hospital References</TabsTrigger>
              <TabsTrigger value="web" className="whitespace-normal py-1.5">Web Search References</TabsTrigger>
            </TabsList>
            <TabsContent value="hospital" forceMount className="mt-3 data-[state=inactive]:hidden">
              {canHospital
                ? <HospitalReferencesPanel active={open && tab === 'hospital'} visitId={visitId} admissionId={admissionId} patientLabel={patientLabel} onInsert={onInsert} />
                : <NoAccess what="Hospital References" />}
            </TabsContent>
            <TabsContent value="web" forceMount className="mt-3 data-[state=inactive]:hidden">
              {canWeb
                ? <WebSearchPanel active={open && tab === 'web'} visitId={visitId} admissionId={admissionId} />
                : <NoAccess what="Web Search References" />}
            </TabsContent>
          </Tabs>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
